import React, { useState } from "react";
import { useEnglishText } from "../../../text/useEnglishText.js";

import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import LockResetIcon from "@mui/icons-material/LockReset";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const SystemCredentialsCard = ({
  credentials,
  onResetPassword,
}) => {
  const { t } = useEnglishText();

  const [resettingPassword, setResettingPassword] =
    useState(false);

  const workspaceUrl =
    credentials?.url ||
    credentials?.URL ||
    "";

  const username =
    credentials?.username ||
    credentials?.USER_NAME ||
    credentials?.DYNASHIP_USER_NAME ||
    "";

  const email =
    credentials?.email ||
    credentials?.EMAIL ||
    credentials?.USER_EMAIL ||
    "";

  const licenseNumber =
    credentials?.licenseNumber ||
    credentials?.license_number ||
    credentials?.LICENSE_NUMBER ||
    "";

  const handleOpenWorkspace = () => {
    if (!workspaceUrl) return;

    const formattedUrl = /^https?:\/\//i.test(workspaceUrl)
      ? workspaceUrl
      : `https://${workspaceUrl}`;

    window.open(
      formattedUrl,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleResetPassword = async () => {
    if (resettingPassword) return;

    if (typeof onResetPassword !== "function") {
      console.error(
        "SystemCredentialsCard: onResetPassword is not defined.",
      );
      return;
    }

    try {
      setResettingPassword(true);

      await onResetPassword({
        email,
        username,
        licenseNumber,
      });
    } catch (error) {
      // نمایش پیام خطا در کامپوننت والد انجام می‌شود.
      console.error(
        "Unable to request password reset:",
        error,
      );
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 2,
            sm: 3,
          },
          "&:last-child": {
            pb: {
              xs: 2,
              sm: 3,
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            {t(
              "licenses.systemCredentials.title",
              "System Credentials",
            )}
          </Typography>

          <TextField
            fullWidth
            label={t(
              "licenses.systemCredentials.workspaceUrl",
              "Workspace URL",
            )}
            value={workspaceUrl}
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />

          <TextField
            fullWidth
            label={t(
              "licenses.systemCredentials.username",
              "Username",
            )}
            value={username}
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />

          <Alert severity="info">
            <Stack spacing={1.5}>
              <Typography variant="body2">
                {t(
                  "licenses.systemCredentials.password.message",
                  "You created this password yourself, and it is the same password you use to sign in to the Admin Center. For security reasons, your password is not displayed here. If you have forgotten it or want to change it, request a reset link and we’ll send it to your email address.",
                )}
              </Typography>

              <Button
                variant="outlined"
                size="small"
                startIcon={
                  resettingPassword ? (
                    <CircularProgress
                      size={16}
                      color="inherit"
                    />
                  ) : (
                    <LockResetIcon />
                  )
                }
                onClick={handleResetPassword}
                disabled={resettingPassword}
                sx={{
                  alignSelf: "flex-start",
                  minWidth: 210,
                }}
              >
                {resettingPassword
                  ? t(
                      "licenses.systemCredentials.password.sending",
                      "Sending Reset Link...",
                    )
                  : t(
                      "licenses.systemCredentials.password.resetButton",
                      "Reset or Change Password",
                    )}
              </Button>
            </Stack>
          </Alert>

          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            onClick={handleOpenWorkspace}
            disabled={!workspaceUrl}
          >
            {t(
              "licenses.systemCredentials.openWorkspace",
              "Open Workspace",
            )}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SystemCredentialsCard;
